import { HttpClient, HttpResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { catchError, map } from 'rxjs/operators'
import { CognitoAuthService } from '../../app/auth/cognito-auth.service'
import { UserAccount, UserAccountPaginatedList, UserAccountSearchCriteria } from '../models/user-account.model'
import { BaseService } from './base.service'

@Injectable({
    providedIn: 'root'
})
export class AccountService extends BaseService {
    get uri(): string {
        return `${this.baseUri}/Account`
    }

    constructor(readonly http: HttpClient, readonly auth: CognitoAuthService) {
        super(http)
    }

    async getAccount(): Promise<UserAccount | undefined> {
        try {
            const response = await this.$http
                .get(`${this.uri}/whoami`, {
                    observe: 'response'
                })
                .toPromise()

            return UserAccount.fromJSON(this.preCheckResponseData(response))
        } catch (error) {
            await this.auth.signOut()

            return undefined
        }
    }

    async getAccounts(accountSearchCriteria: UserAccountSearchCriteria): Promise<UserAccountPaginatedList> {
        return this.$http
            .get<UserAccountPaginatedList>(this.uri, {
                params: accountSearchCriteria.httpParams(),
                observe: 'response'
            })
            .pipe(
                map((response: HttpResponse<UserAccountPaginatedList>) =>
                    UserAccountPaginatedList.fromJSON(this.preCheckResponseData(response))
                ),
                catchError((err) => this.handleError(err))
            )
            .toPromise()
    }

    async getAllAccounts(): Promise<UserAccountPaginatedList> {
        const searchCriteria = new UserAccountSearchCriteria(1, 10000)

        return this.getAccounts(searchCriteria)
    }

    async saveAccount(userAccount: UserAccount): Promise<UserAccount> {
        const uri = userAccount.id ? `${this.uri}/${userAccount.id}` : this.uri
        if (userAccount.id) {
            return this.$http
                .put<UserAccount>(uri, userAccount, { observe: 'response' })
                .pipe(
                    map((response) => UserAccount.fromJSON(this.preCheckResponseData(response))),
                    catchError(async (err) => this.handleError(err))
                )
                .toPromise()
        }

        return this.$http
            .post<UserAccount>(uri, userAccount, { observe: 'response' })
            .pipe(
                map((response) => UserAccount.fromJSON(this.preCheckResponseData(response))),
                catchError(async (err) => this.handleError(err))
            )
            .toPromise()
    }

    async deleteAccount(userAccount: UserAccount): Promise<boolean> {
        const uri = `${this.uri}/${userAccount.id}`

        return this.$http
            .delete(uri)
            .pipe(
                map(() => true),
                catchError(async (err) => this.handleError(err))
            )
            .toPromise()
    }
}
